/**
 * View Configuration - Single source of truth for application views
 *
 * This file defines all available views in the application.
 * Adding a new view requires:
 * 1. Add to VIEW_CONFIGS array below
 * 2. Create the view component
 * 3. Add rendering logic in App.tsx
 *
 * The configuration system allows the ViewToggle component to be
 * data-driven rather than hardcoded.
 */

/** Valid view identifiers */
export type ViewId = 'map' | 'journal';

/** Configuration for a single view */
export interface ViewConfig {
  /** Unique identifier for the view */
  id: ViewId;
  /** Display label for the view tab */
  label: string;
  /** ID for the tab element (for ARIA) */
  tabId: string;
  /** ID for the panel element (for ARIA) */
  panelId: string;
}

/**
 * Configuration for all application views.
 * Order determines display order in the view toggle.
 */
export const VIEW_CONFIGS: readonly ViewConfig[] = [
  {
    id: 'map',
    label: 'Map',
    tabId: 'tab-map',
    panelId: 'panel-map',
  },
  {
    id: 'journal',
    label: 'Journal',
    tabId: 'tab-journal',
    panelId: 'panel-journal',
  },
] as const;

/**
 * Map of view ID to configuration for quick lookup.
 */
export const VIEW_CONFIG_MAP: Record<ViewId, ViewConfig> = Object.fromEntries(
  VIEW_CONFIGS.map(config => [config.id, config])
) as Record<ViewId, ViewConfig>;

/**
 * Get view configuration by ID.
 */
export function getViewConfig(viewId: ViewId): ViewConfig {
  return VIEW_CONFIG_MAP[viewId];
}

/**
 * Get the next view in the list (for keyboard navigation).
 * Wraps around to the first view if at the end.
 */
export function getNextView(currentViewId: ViewId): ViewId {
  const currentIndex = VIEW_CONFIGS.findIndex(v => v.id === currentViewId);
  const nextIndex = (currentIndex + 1) % VIEW_CONFIGS.length;
  return VIEW_CONFIGS[nextIndex].id;
}

/**
 * Get the previous view in the list (for keyboard navigation).
 * Wraps around to the last view if at the beginning.
 */
export function getPreviousView(currentViewId: ViewId): ViewId {
  const currentIndex = VIEW_CONFIGS.findIndex(v => v.id === currentViewId);
  const prevIndex = (currentIndex - 1 + VIEW_CONFIGS.length) % VIEW_CONFIGS.length;
  return VIEW_CONFIGS[prevIndex].id;
}

/**
 * Default view to show on app load.
 */
export const DEFAULT_VIEW: ViewId = 'map';
